import { ListPage, Badge, KpiCard, AsyncBoundary, useToast, Notice } from '@kollez/design-system';
import { useGroupReports, useNirfExport, type ReportRow } from '@/lib/queries';

const STATUS_TONE: Record<ReportRow['status'], 'success' | 'info' | 'warning' | 'outline'> = {
  ready: 'success',
  sent: 'success',
  queued: 'info',
  running: 'info',
  failed: 'warning',
};

export function GroupReportsPage() {
  const { data, isLoading, error, refetch, isFetching } = useGroupReports();
  const nirf = useNirfExport();
  const toast = useToast();
  const rows = data?.reports ?? [];

  async function runNirf() {
    try {
      const r = await nirf.mutateAsync();
      toast.show({
        title: 'NIRF export queued',
        message: `Reference ${r.jobId} · ready around ${new Date(r.readyBy).toLocaleTimeString('en-IN')}`,
      });
    } catch {
      toast.show({ tone: 'warning', message: 'Could not queue NIRF export' });
    }
  }

  const totals = {
    total: rows.length,
    ready: rows.filter((r) => r.status === 'ready').length,
    pending: rows.filter((r) => r.status === 'queued' || r.status === 'running').length,
    failed: rows.filter((r) => r.status === 'failed').length,
  };

  return (
    <div className="stack">
      {/* F-080: heading always visible, even in empty/error state. */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Reports</p>
          <h1 className="h-page">Async exports</h1>
          <p className="subhead">NIRF, NAAC, board packs, raw exports. Anything that runs longer than a request lands here.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => void runNirf()}>Export NIRF now</button>
          <button className="btn btn-ghost" onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>
      <AsyncBoundary
        kind="table"
        loading={isLoading}
        error={error}
        empty={!isLoading && rows.length === 0}
        emptyTitle="No reports yet"
        emptyHint="Queue a NIRF, NAAC, or board scorecard export. It runs asynchronously so it doesn't tie you up."
        emptyAction={<button className="btn btn-primary btn-sm" onClick={() => void runNirf()}>Export NIRF now</button>}
        onRetry={() => void refetch()}
      >
      <div className="stack">
        {totals.pending > 0 && (
          <Notice tone="info">
            <span className="flex-1">
              <b>{totals.pending} report{totals.pending === 1 ? '' : 's'}</b> still generating. This page refreshes automatically every 8s while jobs are active.
            </span>
          </Notice>
        )}

        <ListPage<ReportRow>
          eyebrow="Reports"
          title="Async exports"
          subtitle="NIRF, NAAC, board packs, raw exports. Anything that runs longer than a request lands here."
          actions={[
            { label: 'Export NIRF now', primary: true, onClick: () => void runNirf() },
            { label: isFetching ? 'Refreshing…' : 'Refresh', onClick: () => void refetch() },
          ]}
          kpis={[
            { label: 'Total reports', value: totals.total },
            { label: 'Ready', value: totals.ready, tone: 'success' },
            { label: 'Pending', value: totals.pending, tone: 'info' },
            { label: 'Failed', value: totals.failed, tone: totals.failed ? 'warning' : 'default' },
          ]}
          segment={{
            key: 'status',
            options: [
              ['all', 'All'],
              ['ready', 'Ready'],
              ['queued', 'Queued'],
              ['running', 'Running'],
              ['failed', 'Failed'],
            ],
          }}
          rows={rows}
          rowKey={(r) => r._id}
          columns={[
            {
              key: 'kind',
              label: 'Kind',
              render: (r) => <Badge tone="outline">{r.kind}</Badge>,
            },
            {
              key: 'requestedAt',
              label: 'Requested',
              render: (r) => new Date(r.requestedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
            },
            {
              key: 'rows',
              label: 'Rows',
              right: true,
              hideMobile: true,
              render: (r) => r.rowsIncluded ?? '—',
            },
            {
              key: 'warn',
              label: 'Warnings',
              hideMobile: true,
              render: (r) =>
                r.warnings?.length ? (
                  <Badge tone="warning">{r.warnings.length}</Badge>
                ) : (
                  <span className="text-ink-tertiary">—</span>
                ),
            },
            {
              key: 'status',
              label: 'Status',
              right: true,
              render: (r) => (
                <Badge tone={STATUS_TONE[r.status]}>
                  {r.status}
                </Badge>
              ),
            },
            {
              key: 'act',
              label: '',
              right: true,
              render: (r) =>
                r.status === 'ready' && r.downloadUrl ? (
                  <a className="btn btn-primary btn-sm" href={r.downloadUrl} target="_blank" rel="noreferrer">
                    Download
                  </a>
                ) : r.status === 'failed' ? (
                  <span className="text-ink-warning text-xs">{r.failureReason ?? 'error'}</span>
                ) : (
                  <span className="text-ink-tertiary text-xs">Working…</span>
                ),
            },
          ]}
          drawer={(r) => ({
            eyebrow: r.kind,
            title: r.jobId,
            body: (
              <div className="grid gap-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <KpiCard label="Rows" value={r.rowsIncluded ?? '—'} />
                  <KpiCard label="Format" value={r.format.toUpperCase()} />
                </div>
                {r.warnings?.length ? (
                  <div className="p-3 rounded bg-bg-warning/10 border border-line-tertiary text-xs">
                    <b>Warnings ({r.warnings.length}):</b>
                    <ul className="mt-2 grid gap-1 pl-4 list-disc">
                      {r.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
                      {r.warnings.length > 8 && <li>… + {r.warnings.length - 8} more</li>}
                    </ul>
                  </div>
                ) : null}
                {r.status === 'ready' && r.downloadUrl && (
                  <a className="btn btn-primary" href={r.downloadUrl} target="_blank" rel="noreferrer">
                    Download {r.kind}
                  </a>
                )}
                {r.status === 'failed' && (
                  <div className="p-3 rounded bg-bg-warning/10 border border-line-tertiary text-xs">
                    <b>Failed:</b> {r.failureReason ?? 'unknown reason'}
                  </div>
                )}
              </div>
            ),
          })}
        />
      </div>
      </AsyncBoundary>
    </div>
  );
}
